import { useEffect, useRef, useState } from "react";
import { useInstantSearch } from "react-instantsearch";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { boundingBoxFilter, postMsearch, refinementTermsFilters } from "@/lib/msearch-filters";
import type { MapCluster } from "@/types/search-map";

// The map plots ALL matching results, not just the loaded card page, and clusters them CLIENT-side
// (supercluster) so zooming re-clusters instantly with no per-zoom network re-tile. This hook is the
// data half: it fetches the full match set as a fine-grained POINT FEED — a `geotile_grid` at a
// fixed high precision with `geo_centroid` + `top_hits` sub-aggs, mirroring the cards' facet and
// `insideBoundingBox` filters — once per filter/area change, independent of zoom. `size: 0` keeps
// the agg clear of the proxy's page-size clamp. Listings closer than the fixed cell merge into one
// weighted point (`count > 1`); everything coarser is left to the client clusterer.
const FEED_PRECISION = 16;
const MAX_BUCKETS = 1000;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

function tileYToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

// Geographic bounds of a geotile "z/x/y" cell.
function tileBounds(key: string): { west: number; east: number; north: number; south: number } | null {
  const parts = key.split("/").map(Number);
  if (parts.length !== 3 || parts.some((value) => !Number.isFinite(value))) return null;
  const [z, x, y] = parts as [number, number, number];
  const n = 2 ** z;
  return {
    west: (x / n) * 360 - 180,
    east: ((x + 1) / n) * 360 - 180,
    north: tileYToLat(y, z),
    south: tileYToLat(y + 1, z),
  };
}

interface AggBucket<THit> {
  key: string;
  doc_count: number;
  centroid?: { location?: { lat: number; lon: number } };
  top?: { hits?: { hits?: Array<{ _source?: THit }> } };
}

function bucketToPoint<THit>(bucket: AggBucket<THit>): MapCluster<THit> | null {
  const point = bucket.centroid?.location;
  if (!point) return null;
  // For a multi-valued geo field (programmes), geo_centroid averages ALL of a doc's locations, so a
  // program spanning two continents lands at a mid-ocean midpoint outside this tile. Clamp back into
  // the tile so a point always sits in its own cell's region.
  const bounds = tileBounds(bucket.key);
  const lat = bounds ? clamp(point.lat, bounds.south, bounds.north) : point.lat;
  const lon = bounds ? clamp(point.lon, bounds.west, bounds.east) : point.lon;
  return {
    key: bucket.key,
    lat,
    lon,
    count: bucket.doc_count,
    hit: bucket.top?.hits?.hits?.[0]?._source,
  };
}

export interface UseSearchMapPinsOptions {
  /** msearch endpoint host, e.g. "/api/destinations". */
  host: string;
  /** OpenSearch index name for the msearch header + InstantSearch index id (they match). */
  index: string;
  /** geo_point field: "location" (destinations) / "locations" (programmes). */
  geoField: string;
  /** `_source` fields pulled for each point's representative hit (drives the pin popup). */
  sourceFields: string[];
  /** InstantSearch `insideBoundingBox` string, mirrored so map + cards filter identically. */
  boundsBox?: string | null;
  /** Extra request-body keys the backend rewriter reads (programmes: `{ _travily_when, _travily_who }`). */
  extraBody?: Record<string, unknown>;
  /** Additional OpenSearch filter clauses the cards query also carries (the Where term filter),
   *  so map and cards answer the same question. */
  extraFilters?: Record<string, unknown>[];
  /** Skip the fetch entirely when the map isn't visible (e.g. below the lg breakpoint). */
  enabled?: boolean;
}

export interface UseSearchMapPinsResult<THit> {
  /** Fine-grained feed points for the full match set; the map clusters them client-side. */
  points: MapCluster<THit>[];
  isLoading: boolean;
  /** The pins query failed — distinct from "no results", so the map should not claim it's empty. */
  isError: boolean;
}

export function useSearchMapPins<THit>({
  host,
  index,
  geoField,
  sourceFields,
  boundsBox,
  extraBody,
  extraFilters,
  enabled = true,
}: UseSearchMapPinsOptions): UseSearchMapPinsResult<THit> {
  const csrfToken = useCsrfToken();
  const { uiState } = useInstantSearch();
  const refinementList = uiState?.[index]?.refinementList as Record<string, string[]> | undefined;

  const [points, setPoints] = useState<MapCluster<THit>[]>([]);
  // Start loading so the empty-state overlay never flashes before the first fetch resolves.
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Serialise the object inputs so the effect refetches on a real value change (not the identity
  // churn `uiState` produces every render), while still declaring every dependency the linter wants.
  const refinementKey = JSON.stringify(refinementList ?? null);
  const extraBodyKey = JSON.stringify(extraBody ?? null);
  const extraFiltersKey = JSON.stringify(extraFilters ?? null);

  useEffect(() => {
    if (!enabled) {
      setPoints([]);
      setIsLoading(false);
      setIsError(false);
      return;
    }
    if (!csrfToken) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);

    const refinements = JSON.parse(refinementKey) as Record<string, string[]> | null;
    const extra = JSON.parse(extraBodyKey) as Record<string, unknown> | null;
    const extraClauses = JSON.parse(extraFiltersKey) as Record<string, unknown>[] | null;

    const filter = refinementTermsFilters(refinements);
    if (extraClauses) filter.push(...extraClauses);
    if (boundsBox) {
      const box = boundingBoxFilter(boundsBox, geoField);
      if (box) filter.push(box);
    }

    const body = {
      ...(extra ?? {}),
      size: 0,
      query: filter.length ? { bool: { filter } } : { match_all: {} },
      aggs: {
        map_grid: {
          geotile_grid: { field: geoField, precision: FEED_PRECISION, size: MAX_BUCKETS },
          aggs: {
            centroid: { geo_centroid: { field: geoField } },
            top: { top_hits: { size: 1, _source: sourceFields } },
          },
        },
      },
    };
    postMsearch<{ aggregations?: { map_grid?: { buckets?: AggBucket<THit>[] } } }>(host, index, body, {
      csrfToken,
      signal: controller.signal,
    })
      .then((result) => {
        const buckets = result.aggregations?.map_grid?.buckets ?? [];
        setPoints(buckets.map(bucketToPoint).filter((p): p is MapCluster<THit> => p !== null));
        setIsError(false);
        setIsLoading(false);
      })
      .catch((error) => {
        if ((error as Error).name === "AbortError") return;
        // Keep the last-known points and flag the error so the map doesn't misrender a fake empty
        // state — a failed query is not "no results in this area".
        setIsError(true);
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [
    enabled,
    refinementKey,
    extraBodyKey,
    extraFiltersKey,
    boundsBox,
    csrfToken,
    host,
    index,
    geoField,
    sourceFields,
  ]);

  return { points, isLoading, isError };
}
